import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { connect } from "react-redux";
import { GridLayout } from "../../../common/GridLayout";
import DropDownButton from "devextreme-react/drop-down-button";

import {
    Column,
    DataGrid,
    GroupPanel,
    Grouping,
    FilterRow,
    FilterPanel,
    Sorting,
    Paging,
    Pager,
    LoadPanel,
    Export,
    ColumnHeaderFilter,
    ColumnChooser,
    ColumnChooserSearch,
    HeaderFilter,
    ColumnFixing,
    Scrolling,
    StateStoring,
    Editing,
    Toolbar,
    Item,
    Lookup,
} from "devextreme-react/data-grid";
import GridLayoutManager from "../../../gridLayoutCustomization/GridLayoutManager";
import { Button, Popup } from "devextreme-react";
import { alert, confirm, custom } from "devextreme/ui/dialog";
import { MESSAGES } from "../../../common/Messages";
import RenderSelectBoxCell from "../../../common/RenderCell";
import data from "../../../assets/apiRoutes.json";
import axios from "axios";
import { ItemTypes, SourceTypes, TransactionTypes, YesNo } from "../../../enum/Enum";

const gridKey = "AE4046E0-AB2E-4C69-BDE3-7A69472D1319";
const modules = [{ key: 'I', value: 'IM' },
{ key: 'S', value: 'SI' },
{ key: 'P', value: 'PI' }]
const GenerateItemReorderRecordsGrid = (props) => {
    const detailInDataGrid = useRef(null);
    const detailInDataGridSecond = useRef(null);
    const [selectedRowsData, setSelectedRowsData] = useState([]);
    const [dataSource, setDataSource] = useState(() =>
        props.dataSource ? [...props.dataSource] : []
    );
    const [createdPurchaseOrders, setCreatedPurchaseOrders] = useState([]);
    const [originalDataSource, setOriginalDataSource] = useState(() =>
        props.dataSource ? JSON.parse(JSON.stringify(props.dataSource)) : []
    );
    const [secondGridDataSource, setSecondGridDataSource] = useState([]);
    const [selectedMainRow, setSelectedMainRow] = useState(null);
    const [records] = useState(0);

    const accessToken = useMemo(() => props.oidc?.user?.access_token || '', [props.oidc]);
    const [currentLayout, setCurrentLayout] = useState("Default");

    // Sync dataSource when props change
    useEffect(() => {
        if (props.dataSource) {
            const newData = [...props.dataSource];
            setDataSource(newData);
            setOriginalDataSource(JSON.parse(JSON.stringify(newData)));
        }
    }, [props.dataSource]);

    // Load vendor names when grid loads
    useEffect(() => {
        const loadVendorNames = async () => {
            if (!dataSource || dataSource.length === 0 || !accessToken) return;

            // Find rows that have RVendor but no VendorName
            const rowsNeedingVendorNames = dataSource.filter(
                row => row.RVendor
            );

            if (rowsNeedingVendorNames.length === 0) return;

            try {
                // Make a single API call to get all vendors
                const response = await axios.get(
                    `${window._env_.REACT_APP_APIS}/${data.Common.GetLookup}`,
                    {
                        params: {
                            lookupType: "Vendor",
                            module: "id",
                            isPascalCase: true,
                            dropShip: 'B'
                        }
                    },
                    {
                        headers: {
                            "Content-Type": "application/json",
                            access_token: accessToken,
                            Authorization: `Bearer ${accessToken}`,
                        },
                    }
                );

                // Create a map: Id (RVendor) -> IdentityId (VendorName)
                const vendorNameMap = {};
                if (response.data && Array.isArray(response.data.data)) {
                    response.data.data.forEach(vendor => {
                        // Id is the RVendor, IdentityId is the vendor name
                        if (vendor.Id) {
                            vendorNameMap[vendor.Id] = vendor.IdentityId || '';
                        }
                    });
                }

                // Update dataSource with vendor names by matching RVendor (Id) with vendorNameMap
                setDataSource(prevData => {
                    const hasChanges = prevData.some(row =>
                        row.RVendor && vendorNameMap[row.RVendor]
                    );
                    if (!hasChanges) return prevData;

                    return prevData.map(row => {
                        // Match RVendor (which is Id) with vendorNameMap to get IdentityId (vendor name)
                        if (row.RVendor && vendorNameMap[row.RVendor]) {
                            return { ...row, VendorName: vendorNameMap[row.RVendor] };
                        }
                        return row;
                    });
                });
            } catch (error) {
                console.error('Error loading vendor names:', error);
            }
        };

        loadVendorNames();
    }, [props.dataSource, accessToken]); // Run when props.dataSource or accessToken changes

    const onRowInserting = useCallback((e) => {
        // Add new row to state - DevExtreme will handle the UI, we just sync state
        setDataSource(prevData => [...prevData, e.data]);
    }, []);

    const onRowUpdating = useCallback((e) => {
        // Update row in state - for cell editing, e.newData contains only changed fields
        setDataSource(prevData => {
            const newData = [...prevData];
            const index = newData.findIndex((item) => {
                // Match by reference or by comparing all properties
                if (item === e.oldData) return true;
                // Fallback: try to match by key fields if available
                if (e.key !== undefined) {
                    return item === e.key;
                }
                return false;
            });

            if (index !== -1) {
                // Merge the new data with existing row data
                newData[index] = { ...newData[index], ...e.newData };
            }
            return newData;
        });
    }, []);


    const onDeleteRow = useCallback(async () => {
        const result = await confirm(MESSAGES.DELETE_CONFIRM, MESSAGES.CONFIRM);
        if (result) {
            if (selectedRowsData.length > 0) {
                setDataSource(prevData => prevData.filter((item) => !selectedRowsData.includes(item)));
            }
        }
    }, [selectedRowsData]);

    const loadState = useCallback(() => {
        return GridLayout.sendStorageRequest(gridKey, "GET", accessToken);
    }, [accessToken]);

    const saveState = useCallback((state) => {
        GridLayout.sendStorageRequest(gridKey, "POST", accessToken, state);
    }, [accessToken]);

    const onCreatePurchaseOrders = useCallback(async () => {
        try {
            // Validate that we have data
            if (!dataSource || dataSource.length === 0) {
                alert(MESSAGES.NO_ITEMS_TO_CREATE_PURCHASE_ORDERS, MESSAGES.INFORMATION);
                return;
            }

            // Validation: If any item exists that does not have a vendor assigned, show an error
            const itemsWithoutVendors = dataSource.filter(
                item => !item.RVendor || item.RVendor === '' || item.RVendor === null
            );

            if (itemsWithoutVendors.length > 0) {
                alert(MESSAGES.NO_ITEMS_WITH_VENDORS_SELECTED, MESSAGES.ERROR);
                return;
            }

            // Filter items that have vendors (required for PO creation) - leave as is for downstream mapping
            const itemsWithVendors = dataSource.filter(item =>
                item.RVendor && item.RVendor !== '' && item.RVendor !== null
            );


            // Map dataSource items to CreatePoItem format
            const mappedItems = itemsWithVendors.map(item => ({
                ItemRowId: item.RowId || null, // Item ROWID - you may need to get this from the item lookup
                LotRowId: item.LotRowId || null,
                VendorRowId: item.RVendor,
                ConsigneeRowId: item.RConsignee || null,
                CustomerRowId: item.RCustomer || null,
                VendorItemCode: item.VendorItem || '',
                WarehouseRowId: item.RWarehouse || item.WarehouseId,
                VendorCost: item.VendorCost || null,
                Cost: item.Cost || null,
                OrderQuantity: item.POQty || 0,
                ReorderMultiple: item.ReorderMultiple || null,
                SourceTableName: item.SourceTableName || '',
                SourceRowId: item.RSourceId || null,
                Module: item.Module,
                RequiredDate: item.RequiredDate ? new Date(item.RequiredDate).toISOString() : null,
                ExpectedDate: item.ExpectedDate ? new Date(item.ExpectedDate).toISOString() : null,
                // User-defined fields for drop-ship (if applicable)
                UDC6: item.UserDefinedCode6 || null,
                UDC7: item.UserDefinedCode7 || null,
                UDC8: item.UserDefinedCode8 || null,
                UDC9: item.UserDefinedCode9 || null,
                UDC10: item.UserDefinedCode10 || null,
                UDD6: item.UserDefinedDate6 ? new Date(item.UserDefinedDate6).toISOString() : null,
                UDD7: item.UserDefinedDate7 ? new Date(item.UserDefinedDate7).toISOString() : null,
                UDD8: item.UserDefinedDate8 ? new Date(item.UserDefinedDate8).toISOString() : null,
                UDD9: item.UserDefinedDate9 ? new Date(item.UserDefinedDate9).toISOString() : null,
                UDD10: item.UserDefinedDate10 ? new Date(item.UserDefinedDate10).toISOString() : null,
                UDQ6: item.UserDefinedQuantity6 || null,
                UDQ7: item.UserDefinedQuantity7 || null,
                UDQ8: item.UserDefinedQuantity8 || null,
                UDQ9: item.UserDefinedQuantity9 || null,
                UDQ10: item.UserDefinedQuantity10 || null,
                UDR6: item.UserDefinedReference6 || null,
                UDR7: item.UserDefinedReference7 || null,
                UDR8: item.UserDefinedReference8 || null,
                UDR9: item.UserDefinedReference9 || null,
                UDR10: item.UserDefinedReference10 || null,
            }));

            const createPoRequest = {
                ProfileRowId: props.formFields.PoProfile || null,
                Purpose: TransactionTypes.Shipment,
                GeneratePONumber: true,
                ReferenceNo: props.formFields.Reference || "",
                TransactionDate: props.formFields.TransDate || null,
                DefaultRequiredDate: props.formFields.RequiredDate || null,
                DefaultExpectedDate: props.formFields.ExpectedDate || null,
                DefaultExpirationDate: props.formFields.ExpirationDate || null,
                Description: props.formFields.Description || "",
                Comment: props.formFields.Comment || "",

                FOB: props.fob || "",
                R_FMShipper: props.rFMShipper || null,
                R_ManagementDepartment: props.rManagementDepartment || null,
                R_ManagementJob: props.rManagementJob || null,
                R_ManagementFund: props.rManagementFund || null,
                Details: mappedItems
            };

            props.setLoadIndicatorVisible(true);
            // Call the API
            const response = await axios.post(
                `${window._env_.REACT_APP_APIS}/po/OrderHeader/InsertPurchaseOrderHeader`,
                createPoRequest,
                {
                    headers: {
                        "Content-Type": "application/json",
                        access_token: accessToken,
                        Authorization: `Bearer ${accessToken}`,
                    },
                }
            );

            // Handle response
            if (response.data && response.status === 200) {
                const results = response.data;

                if (results.length > 0) {
                    setCreatedPurchaseOrders(results);
                    setDataSource(prevData =>
                        prevData.filter(item =>
                            !itemsWithVendors.some(createdItem =>
                                createdItem === item ||
                                (createdItem.RowId && createdItem.RowId === item.RowId)
                            )
                        )
                    );
                }
            }

        } catch (error) {
            const errorMessage = error.response?.data?.message ||
                error.response?.data?.error ||
                error.message ||
                MESSAGES.ERROR;
            alert(errorMessage, MESSAGES.ERROR);
        }
        finally {
            props.setLoadIndicatorVisible(false);
        }
    }, [dataSource, accessToken, props]);
    const onCreateRequisitions = useCallback(async () => {
        try {
            // Validate that we have data
            if (!dataSource || dataSource.length === 0) {
                alert(MESSAGES.NO_ITEMS_TO_CREATE_REQUISITIONS, MESSAGES.INFORMATION);
                return;
            }

            // Map dataSource items to CreatePoItem format
            const mappedItems = dataSource.map(item => ({
                ItemRowId: item.RowId || null, // Item ROWID - you may need to get this from the item lookup
                LotRowId: item.LotRowId || null,
                VendorRowId: item.RVendor,
                ConsigneeRowId: item.RConsignee || null,
                CustomerRowId: item.RCustomer || null,
                VendorItemCode: item.VendorItem || '',
                WarehouseRowId: item.RWarehouse || item.WarehouseId,
                VendorCost: item.VendorCost || null,
                Cost: item.Cost || null,
                OrderQuantity: item.POQty || 0,
                ReorderMultiple: item.ReorderMultiple || null,
                SourceTableName: item.SourceTableName || '',
                SourceRowId: item.RSourceId || null,
                Module: item.Module,
                RequiredDate: item.RequiredDate ? new Date(item.RequiredDate).toISOString() : null,
                ExpectedDate: item.ExpectedDate ? new Date(item.ExpectedDate).toISOString() : null,
                
            }));

            const createPoRequest = {
                ProfileRowId: props.formFields.RequisitionProfile || null,
                Purpose: TransactionTypes.Shipment,
                Source: props.formFields.Source || null,
                ReferenceNo: props.formFields.Reference || "",
                RequisitionDate: props.formFields.TransDate || null,
                DefaultRequiredDate: props.formFields.RequiredDate || null,
                DefaultExpectedDate: props.formFields.ExpectedDate || null,
                DefaultExpirationDate: props.formFields.ExpirationDate || null,
                Description: props.formFields.Description || "",
                Comment: props.formFields.Comment || "",
                Department: props.formFields.Department ? String(props.formFields.Department) : null,
                Job: props.formFields.Job ? String(props.formFields.Job) : null,
                Fund: props.formFields.Fund ? String(props.formFields.Fund) : null,

                Details: mappedItems
            };

            props.setLoadIndicatorVisible(true);
            // Call the API
            const response = await axios.post(
                `${window._env_.REACT_APP_APIS}/po/RequisitionHeader/InsertRequisition`,
                createPoRequest,
                {
                    headers: {
                        "Content-Type": "application/json",
                        access_token: accessToken,
                        Authorization: `Bearer ${accessToken}`,
                    },
                }
            );

            // Handle response
            if (response.data && response.status === 200) {
                const results = response.data;

                if (results) {
                    const recordNumber = results.Record || "";
                    const insertedCount = results.InsertedDetailCount || 0;
                    const updatedCount = results.UpdatedDetailCount|| 0;

                    // Create multiline alert message similar to the image
                    const messageHtml = `
                        <div style="padding: 10px;">
                            <div style="margin-bottom: 15px; font-size: 14px;">
                                Requisition Created - Record #:<b> ${recordNumber} </b>
                            </div>
                            <div style="display: flex; gap: 20px; margin-bottom: 10px;">
                                <div>
                                    <label style="font-weight: bold; display: block; margin-bottom: 5px;">Inserted:</label>
                                    <div style="padding: 5px 10px; border: 1px solid #ccc; border-radius: 4px; background-color: #f5f5f5; min-width: 60px; text-align: center;">
                                        ${insertedCount}
                                    </div>
                                </div>
                                <div>
                                    <label style="font-weight: bold; display: block; margin-bottom: 5px;">Updated:</label>
                                    <div style="padding: 5px 10px; border: 1px solid #ccc; border-radius: 4px; background-color: #f5f5f5; min-width: 60px; text-align: center;">
                                        ${updatedCount}
                                    </div>
                                </div>
                            </div>
                        </div>
                    `;

                    custom({
                        title: "Progress",
                        messageHtml: messageHtml,
                        width: 450,
                        height: 200,
                        buttons: [{
                            text: "OK",
                            onClick: () => true
                        }]
                    }).show();

                    setDataSource([]);
                }
            }

        } catch (error) {
            const errorMessage = error.response?.data?.message ||
                error.response?.data?.error ||
                error.message ||
                MESSAGES.ERROR;
            alert(errorMessage, MESSAGES.ERROR);
        }
        finally {
            props.setLoadIndicatorVisible(false);
        }
    }, [dataSource, accessToken, props]);
    const onSaveToFile = useCallback(() => {
        try {
            const dataStr = JSON.stringify(dataSource, null, 2);
            const dataBlob = new Blob([dataStr], { type: 'application/json' });
            const url = URL.createObjectURL(dataBlob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `ItemReorderRecords_${new Date().toISOString().split('T')[0]}.json`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (error) {
            alert(error.message, MESSAGES.ERROR);
        }
    }, [dataSource]);

    const onLoadFromFile = useCallback(() => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.json';
        input.onchange = (e) => {
            const file = e.target.files[0];
            if (file) {
                const reader = new FileReader();
                reader.onload = (event) => {
                    try {
                        const loadedData = JSON.parse(event.target.result);
                        if (Array.isArray(loadedData)) {
                            setDataSource(loadedData);
                            setOriginalDataSource(JSON.parse(JSON.stringify(loadedData)));
                        } else {
                            alert(MESSAGES.INVALID_FILE_FORMAT, MESSAGES.ERROR);
                        }
                    } catch (error) {
                        alert(error.message, MESSAGES.ERROR);
                    }
                };
                reader.readAsText(file);
            }
        };
        input.click();
    }, []);

    const onUndoAllChanges = useCallback(async () => {
        const result = await confirm(MESSAGES.UNDO_ALL_CHANGES_CONFIRM, MESSAGES.CONFIRM);
        if (result) {
            setDataSource(JSON.parse(JSON.stringify(originalDataSource)));
        }
    }, [originalDataSource]);

    // Options menu items
    const optionsMenuItems = useMemo(() => [
        {
            text: props.formFields.PoProfile ? 'Create Purchase Orders' : 'Create Requisition',
            icon: 'plus',
            onClick: props.formFields.PoProfile ? onCreatePurchaseOrders : onCreateRequisitions
        },
        {
            text: 'Load From File...',
            icon: 'folder',
            visible: !props.formFields.IsOpenFromDetails,
            onClick: onLoadFromFile
        },
        {
            text: 'Save to File...',
            icon: 'save',
            visible: !props.formFields.IsOpenFromDetails,
            onClick: onSaveToFile
        },
        {
            text: 'Undo All changes',
            icon: 'undo',
            onClick: onUndoAllChanges
        }
    ], [onCreatePurchaseOrders, onSaveToFile, onLoadFromFile, onUndoAllChanges]);

    // Row styling based on R_VENDOR
    const onRowPrepared = useCallback((e) => {
        if (e.rowType === 'data') {
            const rowData = e.data;
            if (!rowData.RVendor || rowData.RVendor === '' || rowData.RVendor === null) {
                e.rowElement.style.color = 'red';
            } else {
                e.rowElement.style.color = 'black';
            }
        }
    }, []);
    const loadSecondGridData = useCallback(async (transactionDetailRowId) => {
        if (!transactionDetailRowId || !accessToken) {
            setSecondGridDataSource([]);
            return;
        }

        try {
            // API endpoint - adjust the path based on your actual API structure
            const response = await axios.get(
                `${window._env_.REACT_APP_APIS}/om/Transaction/GetTransactionDetailsById`,
                {
                    params: {
                        id: transactionDetailRowId
                    },
                    headers: {
                        access_token: accessToken,
                        Authorization: `Bearer ${accessToken}`,
                    }
                }
            );

            // Handle different response structures
            if (response.data) {
                if (Array.isArray(response.data)) {
                    setSecondGridDataSource(response.data);
                } else if (Array.isArray(response.data.data)) {
                    setSecondGridDataSource(response.data.data);
                } else if (response.data.result && Array.isArray(response.data.result)) {
                    setSecondGridDataSource(response.data.result);
                } else {
                    setSecondGridDataSource([]);
                }
            } else {
                setSecondGridDataSource([]);
            }
        } catch (error) {
            console.error('Error loading second grid data:', error);
            setSecondGridDataSource([]);
        }
    }, [accessToken]);

    const onSelectionChanged = useCallback((e) => {
        setSelectedRowsData(e.selectedRowsData);

        // Load second grid data when a row is selected
        if (e.selectedRowsData && e.selectedRowsData.length > 0) {
            const selectedRow = e.selectedRowsData[0];
            // Try common field names for transaction detail row ID
            const transactionDetailRowId = selectedRow.RSourceId;
            setSelectedMainRow(selectedRow);
            if (transactionDetailRowId) {
                loadSecondGridData(transactionDetailRowId);
            } else {
                setSecondGridDataSource([]);
            }
        } else {
            setSelectedMainRow(null);
            setSecondGridDataSource([]);
        }
    }, [loadSecondGridData]);
    const fetchVendorCrossReferenceDetails = useCallback(async (itemRowId, vendorId) => {
        try {
            const response = await axios.get(
                `${window._env_.REACT_APP_APIS}/im/VenorCrDetail/GetVendorCrDetails`,
                {
                    params: {
                        ItemRowId: itemRowId,
                        RVendor: vendorId,
                    },
                    headers: {
                        "Content-Type": "application/json",
                        access_token: accessToken,
                        Authorization: `Bearer ${accessToken}`,
                    }
                }
            );
            return response;
        } catch (error) {
            console.error('Error fetching vendor cross reference details:', error);
            return null;
        }
    }, [accessToken]);
        const onEditingStart = (e) => {
        if(e.key != null && e.key != undefined && !detailInDataGrid.current.instance().isRowSelected(e.key))
        {
            detailInDataGrid.current.instance().selectRows([e.key], false);
        }
    };
    return (
        <div >
            <DataGrid
                onExporting={(e) => {
                    if (window.handleDevExtremeDataGridExport) {
                        window.handleDevExtremeDataGridExport(e);
                    }
                }}
                dataSource={dataSource}
                showBorders={true}
                onSelectionChanged={onSelectionChanged}
                onEditingStart={onEditingStart}
                ref={detailInDataGrid}
                hoverStateEnabled={true}
                allowColumnResizing={true}
                allowColumnReordering={true}
                columnAutoWidth={true}
                showRowLines={true}
                onRowPrepared={onRowPrepared}
                selection={{ mode: 'single' }}
                repaintChangesOnly={true}
                height="65vh"
            >
                <Toolbar>
                    <Item location="before">

                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '15px' }}>
                            <span style={{ display: 'inline-block', width: 16, height: 16, background: 'black', marginRight: 4 }}></span>
                            Lines with vendors
                            <span style={{ display: 'inline-block', width: 16, height: 16, background: 'red', margin: '0 4px 0 18px' }}></span>
                            Lines without vendors
                        </span>
                    </Item>

                    <Item
                        name="addRowButton"
                        disabled={props.disableGridsForRFTransactions === true}

                    />
                    <Item location="after">
                        <Button
                            onClick={onDeleteRow}
                            icon="trash"
                        />
                    </Item>
                    <Item location="after">
                        <DropDownButton
                            icon="fa fa-cogs"
                            stylingMode="text"
                            items={optionsMenuItems}
                            dropDownOptions={{ width: 'auto', minWidth: 300 }}
                            displayExpr="text"
                        />

                    </Item>
                    <Item location="after">
                        <GridLayoutManager gridKey={gridKey} dataGrid={detailInDataGrid} setCurrentLayout={setCurrentLayout} />
                    </Item>
                    <Item name="exportButton" />
                    <Item name="columnChooserButton" />
                </Toolbar>
                <Editing
                    mode="cell"
                    allowAdding={true}
                    allowUpdating={true}
                    useIcons={true}
                />
                <StateStoring
                    enabled={true}
                    type="custom"
                    customLoad={loadState}
                    storageKey={gridKey}
                    customSave={saveState}
                />
                <ColumnChooser
                    enabled={true}
                    title="Choose Column"
                    width={290}
                    height={290}
                    sortOrder="asc"
                >
                    <ColumnChooserSearch
                        enabled={true}
                        editorOptions={{ placeholder: 'Search column' }} />
                </ColumnChooser>
                <ColumnFixing enabled={true} />
                <ColumnHeaderFilter />
                <Export enabled={true} allowExportSelectedData={true} text="Export" />
                <Sorting visible={true} mode="multiple" />
                <LoadPanel enabled={true} />
                <Paging enabled={false} />
                <FilterRow visible={true} />
                <HeaderFilter />
                <GroupPanel visible={true} />
                <Grouping autoExpandAll={false} />
                <Scrolling mode="virtual" />
                <StateStoring
                    enabled={true}
                    type="custom"
                    customLoad={loadState}
                    storageKey={gridKey}
                    customSave={saveState}
                />
                <Column
                    dataField="Module"
                    caption="Type"
                >
                    <Lookup
                        dataSource={modules}
                        displayExpr="value"
                        valueExpr="key"
                    />
                </Column>
                <Column
                    dataField="WarehouseId"
                    caption="Warehouse"
                    allowSorting={false}
                    editCellRender={(cellElement) =>
                        RenderSelectBoxCell({
                            loadUrl: `${window._env_.REACT_APP_APIS}/id/Lookup/GetWarehouses`,
                            key: "id",
                            displayExpr: "name",
                            valueExpr: "id",
                            accessToken: accessToken,
                            defaultValue: cellElement.data.WarehouseId,
                            sort: [{ selector: "name", desc: false }],
                            onValueChangeCallback: (value, text, additionalData) => {
                                cellElement.setValue(({ value, text, additionalData }));
                            },
                        })
                    }
                    setCellValue={(rowData, newValue) => {
                        rowData.RWarehouse = newValue.value;
                        rowData.WarehouseId = newValue.text;
                    }}
                />
                <Column
                    dataField="ItemCode"
                    caption="Item"
                    editCellRender={(cellElement) =>
                        RenderSelectBoxCell({
                            loadParams: {
                                lookupType: function () {
                                    return "ImItem";
                                },
                                module: function () {
                                    return "im";
                                }
                                ,
                                moduleName: function () {
                                    return "IM";
                                },
                                isPascalCase: function () {
                                    return true;
                                },
                                itemType: function () {
                                    return '';
                                },
                                runtimeParameter: function () {
                                    return JSON.stringify([
                                        { RWarehouse: cellElement.data.RWarehouse },
                                    ]);
                                }
                            },
                            loadUrl: `${window._env_.REACT_APP_APIS}/${data.Common.GetLookup}`,
                            key: "Id",
                            displayExpr: "Name",
                            valueExpr: "Id",
                            accessToken: accessToken,
                            defaultValue: cellElement.data.ItemCode,
                            sort: [{ selector: "Name", desc: false }],
                            onValueChangeCallback: (value, text, additionalData) => {
                                cellElement.setValue(({ value, text, additionalData }));
                            },
                        })
                    }
                    setCellValue={(rowData, newValue) => {
                        rowData.ItemCode = newValue.text;
                        rowData.RowId = newValue.value;
                    }}
                />
                <Column
                    dataField="LotCode"
                    caption="Lot"
                    allowEditing={true}
                    editCellRender={(cellElement) =>
                        RenderSelectBoxCell({
                            loadParams: {
                                lookupType: function () {
                                    return "ImLot";
                                },
                                module: function () {
                                    return "im";
                                }
                                ,
                                moduleName: function () {
                                    return "IM";
                                },
                                isPascalCase: function () {
                                    return true;
                                },
                                rItem: function () {
                                    return cellElement.data.RowId;
                                }
                            },
                            loadUrl: `${window._env_.REACT_APP_APIS}/${data.Common.GetLookup}`,
                            key: "Codevalue",
                            displayExpr: "Codevalue",
                            valueExpr: "Id",
                            accessToken: accessToken,
                            defaultValue: cellElement.data.LotCode,
                            sort: [{ selector: "Codevalue", desc: false }],
                            onValueChangeCallback: (value, text, additionalData) => {
                                cellElement.setValue(({ value, text, additionalData }));
                            },
                        })
                    }
                    setCellValue={(rowData, newValue) => {
                        rowData.LotCode = newValue.text;
                        rowData.LotRowId = newValue.value;
                    }}
                />
                <Column
                    dataField="VendorName"
                    caption="Vendor"
                    cellRender={(cellData) => {
                        // Display the name if available, otherwise display ItemCode
                        return cellData.data.VendorName || '';
                    }}
                    editCellRender={(cellElement) =>
                        RenderSelectBoxCell({
                            loadParams: {
                                lookupType: function () {
                                    return "Vendor";
                                },
                                module: function () {
                                    return "id";
                                }
                                ,
                                isPascalCase: function () {
                                    return true;
                                },
                                dropShip: function () {
                                    return 'B';
                                }
                            },
                            loadUrl: `${window._env_.REACT_APP_APIS}/${data.Common.GetLookup}`,
                            key: "Id",
                            displayExpr: "IdentityId",
                            valueExpr: "Id",
                            accessToken: accessToken,
                            defaultValue: cellElement.data.RVendor,
                            sort: [{ selector: "IdentityId", desc: false }],
                            onValueChangeCallback: async (value, text, additionalData) => {
                                const response = await fetchVendorCrossReferenceDetails(cellElement.data.RowId, value);
                                if (response && response.data && response.data.length === 0) {
                                    alert(MESSAGES.NO_VENDOR_CROSS_REFERENCE_DATA, MESSAGES.INFORMATION);
                                }
                                else {
                                    additionalData.VendorCost = response.data[0].PurchaseCost;
                                    additionalData.VendorItem = response.data[0].VendorItemCode;
                                }
                                cellElement.setValue(({ value, text, additionalData }));
                            },
                        })
                    }
                    setCellValue={(rowData, newValue) => {
                        rowData.VendorCost = newValue.additionalData.VendorCost;
                        rowData.VendorItem = newValue.additionalData.VendorItem;
                        rowData.RVendor = newValue.value;
                        rowData.VendorName = newValue.text;
                    }}
                />
                <Column
                    dataField="VendorItem"
                    caption="Vendor Item"
                />
                <Column
                    dataField="VendorCost"
                    caption="Vendor Cost"
                    dataType="number"
                    format="#,##0.0000"
                />
                <Column
                    dataField="MinimumQty"
                    caption="Minimum Qty"
                    dataType="number"
                    format="#,##0.0000"
                    allowEditing={false} visible={false}
                />
                 <Column
                    dataField="POQty"
                    caption={props.formFields.Source === SourceTypes.Requisition ? "Required Qty" : "PO Qty"}
                    dataType="number"
                    format="#,##0.0000"
                />
                <Column
                    dataField="OrderQty"
                    caption="OM Ordered"
                    dataType="number"
                    format="#,##0.0000"
                    allowEditing={false}
                />
                <Column
                    dataField="OnHand"
                    caption="On Hand"
                    dataType="number"
                    format="#,##0.00"
                    allowEditing={false}
                />
                <Column
                    dataField="Available"
                    caption="Available"
                    dataType="number"
                    format="#,##0.00"
                    allowEditing={false}
                />
                <Column
                    dataField="NetAvailable"
                    caption="Net Available"
                    dataType="number"
                    format="#,##0.00"
                    allowEditing={false}
                />

                <Column
                    dataField="ReorderMethod"
                    caption="Method"
                    allowEditing={false} 
                >
                    <Lookup
                        dataSource={[{ id: 'M', text: 'Minimum Quantity' }, { id: 'D', text: 'Minimum Days' },{ id: 'F', text: 'By Fixed Date' }, { id: 'N', text: 'Not Applicable' }]}
                        displayExpr="text"
                        valueExpr="id" />
                </Column>
                <Column
                    dataField="ReorderMultiple"
                    caption="Reorder Multiple"
                    dataType="number"
                    format="#,##0.00"
                    allowEditing={false} visible={false}
                />
                <Column
                    dataField="SafetyQty"
                    caption="Safety Qty"
                    dataType="number"
                    format="#,##0.00"
                    allowEditing={false} visible={false}
                />

                <Column
                    dataField="ItemDesc1"
                    caption="Item Desc 1"
                    allowEditing={false} visible={false}
                />
                <Column
                    dataField="ItemDesc2"
                    caption="Item Desc 2"
                    allowEditing={false} visible={false}
                />
                <Column
                    dataField="Suggested"
                    caption="Suggested"
                    dataType="number"
                    format="#,##0.00"
                    allowEditing={false} visible={false}
                />

                <Column
                    dataField="Cost"
                    caption="Cost"
                    dataType="number"
                    format="#,##0.00"
                    allowEditing={false} visible={false}
                />
                <Column
                    dataField="SourceTableName"
                    caption="Source Table Name"
                    allowEditing={false} visible={false}
                />

                <Column
                    dataField="PurchaseUom"
                    caption="Purchase UOM"
                    allowEditing={false} visible={false}
                />

                <Column
                    dataField="ReorderQuantityEntered"
                    caption="Reorder Quantity Entered"
                    dataType="number"
                    format="#,##0.00"
                    allowEditing={false} visible={false}
                />
                <Column
                    dataField="UserDefinedReference1"
                    caption="User Defined Reference 1"
                    allowEditing={false} visible={false}
                />
                <Column
                    dataField="LocationCode"
                    caption="Location Code"
                    allowEditing={false} visible={false}
                />
                <Column
                    dataField="LocationDesc"
                    caption="Location Desc"
                    allowEditing={false} visible={false}
                />
                <Column
                    dataField="PriceCode"
                    caption="Price Code"
                    allowEditing={false} visible={false}
                />
                <Column
                    dataField="PriceDescription"
                    caption="Price Description"
                    allowEditing={false} visible={false}
                />

                <Column
                    dataField="RequiredDate"
                    caption="Required Date"
                    dataType="datetime"
                    format="MM/dd/yyyy"
                    allowEditing={false} visible={false}
                />
                <Column
                    dataField="ExpectedDate"
                    caption="Expected Date"
                    dataType="datetime"
                    format="MM/dd/yyyy"
                    allowEditing={false} visible={false}
                />
                <Column
                    dataField="MaximumQty"
                    caption="Maximum Qty"
                    dataType="number"
                    format="#,##0.00"
                    allowEditing={false} visible={false}
                />
                <Column
                    dataField="UnitsPerPackageQty"
                    caption="Units Per Package Qty"
                    dataType="number"
                    format="#,##0.00"
                    allowEditing={false} visible={false}
                />
                <Column
                    dataField="InFuture"
                    caption="In Future"
                    dataType="number"
                    format="#,##0.00"
                    allowEditing={false} visible={false}
                />
                <Column
                    dataField="InReceiving"
                    caption="In Receiving"
                    dataType="number"
                    format="#,##0.00"
                    allowEditing={false} visible={false}
                />
                <Column
                    dataField="InOrdered"
                    caption="In Ordered"
                    dataType="number"
                    format="#,##0.00"
                    allowEditing={false} visible={false}
                />
                <Column
                    dataField="ManufacturerId"
                    caption="Manufacturer ID"
                    allowEditing={false} visible={false}
                />
                <Column
                    dataField="ManufacturerName"
                    caption="Manufacturer Name"
                    allowEditing={false} visible={false}
                />

            </DataGrid>
            <div style={{ height: '10px' }}></div>
           {
            !props.formFields.IsOpenFromDetails && (
            <DataGrid
                dataSource={secondGridDataSource}
                showBorders={true}
                ref={detailInDataGridSecond}
                hoverStateEnabled={true}
                allowColumnResizing={true}
                allowColumnReordering={true}
                columnAutoWidth={true}
                showRowLines={true}
                height="20vh"
            >
                <ColumnChooser enabled={true} />
                <ColumnFixing enabled={true} />
                <Export enabled={true} allowExportSelectedData={true} text="Export" />
                <Sorting visible={true} mode="multiple" />
                <LoadPanel enabled={true} />
                <Paging enabled={false} />
                <FilterRow visible={true} />
                <Scrolling mode="virtual" />
                <Toolbar>
                    <Item location="before">

                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '15px',fontWeight:'bold' }}>
                          OM Transaction 
                        </span>
                    </Item>
                    </Toolbar>
                <Column
                    dataField="CUSTOMERIDENTITYID"
                    caption="Customer"
                />
                <Column
                    dataField="SHIPTOID"
                    caption="Ship To"
                />
                <Column
                    dataField="TRANSACTIONNUMBER"
                    caption="Transaction #"
                />
                <Column
                    dataField="TRANSACTIONDATE"
                    caption="Transaction Date"
                    dataType="datetime"
                    format="MM/dd/yyyy"
                />
                <Column
                    dataField="INVOICEDATE"
                    caption="Invoice Date"
                    dataType="datetime"
                    format="MM/dd/yyyy"
                />
                <Column
                    dataField="SHIPPINGDATE"
                    caption="Shipping Date - Detail"
                    dataType="datetime"
                    format="MM/dd/yyyy"
                />
            </DataGrid>
            )
           }
            
            {createdPurchaseOrders.length > 0 && (
                <Popup
                    visible={createdPurchaseOrders.length > 0}
                    title="Created Purchase Orders"
                    showCloseButton={true}
                    showTitle={true}
                    width="50vw"
                    height="50vh"
                    container="#root"
                    className="custom-popup"
                    closeOnOutsideClick={false}
                    onHiding={() => setCreatedPurchaseOrders([])}
                >
                    <div>
                        <DataGrid
                            dataSource={createdPurchaseOrders}
                            showBorders={true}
                            hoverStateEnabled={true}
                            allowColumnResizing={true}
                            allowColumnReordering={true}
                            columnAutoWidth={true}
                            showRowLines={true}
                            height="40vh">
                            <Column dataField="Record" caption="Record #" dataType="string" />
                            <Column dataField="PONumber" caption="PO #" dataType="string" />
                            <Column dataField="VendorCode" caption="Vendor " dataType="string" />
                            <Column dataField="ConsigneeCode" caption="Consignee" dataType="string" />
                            <Column dataField="DetailCount" caption="# Detail Lines Created" dataType="number" format="#,##0.00" />
                        </DataGrid>

                    </div>
                </Popup>
            )}
        </div>
    );
};
function mapStateToProps(state) {
    return {
        ReceiptReducer: state.ReceiptReducer,
        oidc: state.oidc,
    };
}

export default connect(mapStateToProps)(GenerateItemReorderRecordsGrid);